/**
 * Render-path bridge between `ModifierGeometry` (the typed-array pipeline
 * currency) and `THREE.BufferGeometry` (the GPU-oriented Three.js struct).
 *
 * The modifier pipeline operates exclusively on `ModifierGeometry`.  Callers
 * that render to the GPU build a `ModifierGeometry` from their source data
 * (a `SceneObject` or a blend-file mesh), run it through `applyModifiers`,
 * and then finalize to a `THREE.BufferGeometry` at the end.  This module
 * owns both endpoints so the round-trip semantics stay consistent.
 *
 * Two finalize variants are provided because render and scene paths have
 * genuinely different post-processing needs:
 *
 *   - `finalizePerCorner`  — expands shared vertices to per-corner arrays
 *                            and recomputes loop normals with an auto-smooth
 *                            angle.  Used by `PrimitiveRenderer` and the
 *                            path-trace static mesh path.
 *
 *   - `finalizeSharedVertex` — keeps shared-vertex layout and uses welded
 *                              normals.  Used by `SceneRenderer` for
 *                              blend-file meshes where morph targets and
 *                              skinning live on the same attribute layout
 *                              as the modifier pipeline output.
 */

import * as THREE from "three/webgpu";
import {
  buildSharedVertexIndices,
  expandForRendering,
  perVertexUVsFromLoopUVs,
} from "../../modeling/loopExpand.ts";
import { applyFaceShadingOverride } from "../../modeling/ops/normals.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import { triangulateTopologyForRendering } from "../../modeling/topology.ts";
import {
  type PolygonTopology,
  resolveFaceMaterialIndices,
  type SceneObject,
} from "../../modeling/types.ts";
import { recomputeLoopNormals } from "../../wasm/normals.ts";
import {
  type GeometryGroup,
  type ModifierGeometry,
  resolveModifierGeometryTopology,
} from "./modifierTypes.ts";

// ---------------------------------------------------------------------------
// Build: SceneObject → ModifierGeometry
// ---------------------------------------------------------------------------

/**
 * Build a complete `ModifierGeometry` from a `SceneObject`, suitable for
 * input to `applyModifiers` on the render path.
 *
 * Arrays are referenced directly from `obj` (no clone).  This keeps the
 * modifier pipeline's `WeakMap<Float32Array, ...>` cache keyed on the stable
 * `obj.vertices` reference so repeated renders hit the cache.  Safe because
 * all modifier cores are pure: they allocate new output arrays rather than
 * mutating their input.
 *
 * `topology` is also referenced directly — same reasoning.  If a future
 * modifier ever needs to mutate topology in place, this will need to change.
 */
export function buildRenderModifierGeometry(
  obj: SceneObject,
): ModifierGeometry {
  const vertexCount = obj.vertices.length / 3;
  const faceMaterialIndices = resolveFaceMaterialIndices(obj);
  return {
    positions: obj.vertices,
    indices: buildSharedVertexIndices(obj.indices, obj.topology),
    normals: null,
    uvs:
      obj.uvs && obj.uvs.length > 0
        ? perVertexUVsFromLoopUVs(obj.uvs, obj.topology, vertexCount)
        : null,
    morphArrays: [],
    skinIndex: null,
    skinWeight: null,
    sculptDisplacement: null,
    groups: faceMaterialIndices
      ? buildGeometryGroupsFromFaceMaterialIndices(
          faceMaterialIndices,
          obj.topology,
        )
      : [],
    faceAttributes: obj.faceAttributes,
    edges: obj.topology.edges.length > 0 ? obj.topology.edges : null,
    edgeFlags: obj.topology.edges.length > 0 ? obj.topology.edgeFlags : null,
    edgeCreases:
      obj.topology.edges.length > 0 && obj.topology.edgeCreases
        ? obj.topology.edgeCreases
        : null,
    skinRadii: obj.skinRadii ?? null,
    topology: obj.topology,
    provenance: identityProvenance(vertexCount),
  };
}

/**
 * Convert a per-face slot-index array into index-buffer material groups.
 * Each polygon face fans into `faceSize - 2` triangles, and the renderer's
 * triangle index buffers are face-ordered, so contiguous faces with the same
 * slot can be merged into a single draw group.
 */
export function buildGeometryGroupsFromFaceMaterialIndices(
  faceMaterialIndices: Uint16Array,
  topology: PolygonTopology,
): GeometryGroup[] {
  const groups: GeometryGroup[] = [];
  const { offsets } = topology;
  const faceCount = offsets.length - 1;
  if (faceCount === 0) return groups;

  if (faceMaterialIndices.length !== faceCount) {
    const total = triangleIndexCountForTopology(topology);
    return total > 0 ? [{ start: 0, count: total, materialIndex: 0 }] : groups;
  }

  let runMat = faceMaterialIndices[0];
  let idxCursor = 0;
  let runStart = 0;

  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    const triCount = size >= 3 ? size - 2 : 0;
    const numIdx = triCount * 3;
    const mat = faceMaterialIndices[f];
    if (mat !== runMat) {
      const runLen = idxCursor - runStart;
      if (runLen > 0) {
        groups.push({
          start: runStart,
          count: runLen,
          materialIndex: runMat,
        });
      }
      runMat = mat;
      runStart = idxCursor;
    }
    idxCursor += numIdx;
  }
  const tailLen = idxCursor - runStart;
  if (tailLen > 0) {
    groups.push({ start: runStart, count: tailLen, materialIndex: runMat });
  }

  return groups;
}

function triangleIndexCountForTopology(topology: PolygonTopology): number {
  const { offsets } = topology;
  let total = 0;
  for (let f = 0; f < offsets.length - 1; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) total += (size - 2) * 3;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Finalize: ModifierGeometry → THREE.BufferGeometry (per-corner expansion)
// ---------------------------------------------------------------------------

/**
 * Finalize a modifier-pipeline output into a GPU-ready `BufferGeometry` by
 * expanding shared vertices to per-corner arrays and computing loop normals
 * with an auto-smooth angle.
 *
 * This is the hi-fidelity path used by `PrimitiveRenderer` and the
 * path-trace static mesh builder.  It produces per-corner position/normal/uv
 * arrays so hard edges and auto-smoothing render correctly.
 *
 * When `modGeo.topology` is absent (after a retopologizing modifier like
 * subsurf or skin), triangle topology is reconstructed from `modGeo.indices`
 * as a fallback.
 *
 * When `faceMaterialIndices` is supplied, contiguous runs of same-slot faces
 * are emitted as `geometry.addGroup()` calls so THREE.js can route each run
 * to the correct material in a `Material[]` assignment. This is the primitive
 * side of Blender-style multi-material slots; faces map 1:1 to triangle runs
 * because `triangulateTopologyForRendering` is face-ordered.
 */
export function finalizePerCorner(
  modGeo: ModifierGeometry,
  autoSmoothAngle: number,
  faceMaterialIndices?: Uint16Array,
  faceShading?: Uint8Array,
): THREE.BufferGeometry {
  const topology = topologyForModifierGeometry(modGeo);

  const loopNormals = recomputeLoopNormals(
    modGeo.positions,
    topology,
    autoSmoothAngle,
  );
  if (faceShading && faceShading.length > 0) {
    applyFaceShadingOverride(loopNormals, modGeo.positions, topology, {
      shading: faceShading,
    });
  }

  // Expand per-vertex UVs to per-corner through the topology corners
  let cornerUVs: Float32Array | undefined;
  if (modGeo.uvs) {
    cornerUVs = new Float32Array(topology.corners.length * 2);
    for (let c = 0; c < topology.corners.length; c++) {
      const vi = topology.corners[c];
      cornerUVs[c * 2] = modGeo.uvs[vi * 2];
      cornerUVs[c * 2 + 1] = modGeo.uvs[vi * 2 + 1];
    }
  }

  const expanded = expandForRendering(
    modGeo.positions,
    loopNormals,
    topology,
    cornerUVs,
  );

  const result = new THREE.BufferGeometry();
  result.setAttribute(
    "position",
    new THREE.BufferAttribute(expanded.renderVertices, 3),
  );
  result.setAttribute(
    "normal",
    new THREE.BufferAttribute(expanded.renderNormals, 3),
  );
  if (expanded.renderUVs) {
    result.setAttribute("uv", new THREE.BufferAttribute(expanded.renderUVs, 2));
  }
  result.setIndex(
    new THREE.BufferAttribute(triangulateTopologyForRendering(topology), 1),
  );

  if (faceMaterialIndices) {
    applyFaceMaterialGroups(result, topology, faceMaterialIndices);
  } else if (modGeo.groups.length > 0) {
    // Apply groups baked into the modifier geometry (e.g. from the instance
    // modifier, which duplicates source-geometry groups across copies).
    // Group start/count positions are valid in the per-corner index buffer
    // because fan-triangulation assigns the same triangle count per face in
    // both shared-vertex and per-corner layouts.
    applyGeometryGroupsWithDefaultFill(result, modGeo.groups);
  }

  return result;
}

/**
 * Apply pipeline-propagated material groups and fill any uncovered index
 * ranges with material slot 0. Some modifiers preserve source groups for the
 * unchanged faces while generating new faces without explicit material data;
 * with a THREE.Material[] assignment, uncovered ranges do not render at all.
 */
export function applyGeometryGroupsWithDefaultFill(
  geometry: THREE.BufferGeometry,
  groups: readonly GeometryGroup[],
): void {
  if (groups.length === 0) return;

  const total =
    geometry.index?.count ??
    (geometry.getAttribute("position") as THREE.BufferAttribute | undefined)
      ?.count ??
    0;
  if (total <= 0) return;

  const sorted = groups
    .filter((g) => g.count > 0 && g.start < total)
    .slice()
    .sort((a, b) => a.start - b.start);
  if (sorted.length === 0) {
    geometry.addGroup(0, total, 0);
    return;
  }

  let cursor = 0;
  for (const g of sorted) {
    const start = Math.max(0, Math.min(total, Math.trunc(g.start)));
    const requestedEnd = start + Math.max(0, Math.trunc(g.count));
    const end = Math.max(start, Math.min(total, requestedEnd));
    if (end <= cursor) continue;

    if (start > cursor) {
      geometry.addGroup(cursor, start - cursor, 0);
    }

    const groupStart = Math.max(start, cursor);
    geometry.addGroup(groupStart, end - groupStart, g.materialIndex ?? 0);
    cursor = end;
  }

  if (cursor < total) {
    geometry.addGroup(cursor, total - cursor, 0);
  }
}

/**
 * Resolve the polygon topology to use for render-side edge/face work.
 *
 * Retopologizing modifiers may omit `modGeo.topology`; in that case we
 * reconstruct triangle topology from the shared-vertex index buffer so
 * wireframe and face-group logic stay aligned with the evaluated mesh.
 */
export function topologyForModifierGeometry(
  modGeo: ModifierGeometry,
): PolygonTopology {
  return resolveModifierGeometryTopology(modGeo);
}

/**
 * Emit `addGroup` calls on `geometry` so that contiguous runs of same-slot
 * faces each become one draw-call range. Requires that the geometry's index
 * buffer is face-ordered (fan triangulation of the same `topology`), which is
 * the layout produced by `triangulateTopologyForRendering` and the primitive
 * geometry generators.
 *
 * When `faceMaterialIndices.length` differs from the topology's face count
 * (e.g. because a modifier changed face count and the caller held on to the
 * pre-modifier indices), the function degrades gracefully to a single group
 * of slot 0 covering the entire mesh — callers should clear stale per-face
 * data via `reconcileMeshAttributes` before this mismatch can occur.
 */
export function applyFaceMaterialGroups(
  geometry: THREE.BufferGeometry,
  topology: PolygonTopology,
  faceMaterialIndices: Uint16Array,
): void {
  const { offsets } = topology;
  const faceCount = offsets.length - 1;
  if (faceCount === 0) return;

  if (faceMaterialIndices.length !== faceCount) {
    // Stale — fall through to a single slot-0 group so rendering stays safe.
    const total = geometry.index ? geometry.index.count : 0;
    if (total > 0) geometry.addGroup(0, total, 0);
    return;
  }

  let runMat = faceMaterialIndices[0];
  let triCursor = 0;
  let runTriStart = 0;

  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    const triCount = size >= 3 ? size - 2 : 0;
    const mat = faceMaterialIndices[f];
    if (mat !== runMat) {
      const runLen = triCursor - runTriStart;
      if (runLen > 0) geometry.addGroup(runTriStart * 3, runLen * 3, runMat);
      runMat = mat;
      runTriStart = triCursor;
    }
    triCursor += triCount;
  }
  const tailLen = triCursor - runTriStart;
  if (tailLen > 0) geometry.addGroup(runTriStart * 3, tailLen * 3, runMat);
}

// ---------------------------------------------------------------------------
// Finalize: ModifierGeometry → THREE.BufferGeometry (shared-vertex layout)
// ---------------------------------------------------------------------------

/** Optional extras to attach to a shared-vertex render geometry. */
export interface SharedVertexExtras {
  /** Morph target position deltas (per-vertex, stride 3). */
  morphArrays?: Float32Array[];
  /** Corresponding morph target names.  Written to `userData.morphNames`. */
  morphNames?: string[];
  /** Whether morph deltas are relative to the base (true) or absolute (false). */
  morphTargetsRelative?: boolean;
  /** Material groups (start, count, materialIndex). */
  groups?: GeometryGroup[];
}

/**
 * Finalize a `ModifierGeometry` into a GPU-ready `BufferGeometry` without
 * per-corner expansion.  Used by `SceneRenderer` for blend-file meshes and
 * the path-trace blend mesh builder, where the modifier output's
 * shared-vertex layout maps directly to the render attributes.
 *
 * Normals are NOT set here — callers are expected to call
 * `computeWeldedNormals(result)` afterward so seam shading is consistent.
 * If the `modGeo` already carries normals (e.g. from an upstream step),
 * those are used verbatim and welding is skipped.
 *
 * Morph targets, material groups, and other extras are provided via `extras`
 * when present on the source mesh.  They do not flow through the modifier
 * pipeline at this layer (that's handled by the modifier cores themselves
 * when they operate on a ModifierGeometry that already contains them).
 */
export function finalizeSharedVertex(
  modGeo: ModifierGeometry,
  extras?: SharedVertexExtras,
): THREE.BufferGeometry {
  const result = new THREE.BufferGeometry();
  result.setAttribute(
    "position",
    new THREE.BufferAttribute(modGeo.positions, 3),
  );
  if (modGeo.normals) {
    result.setAttribute("normal", new THREE.BufferAttribute(modGeo.normals, 3));
  }
  if (modGeo.uvs) {
    result.setAttribute("uv", new THREE.BufferAttribute(modGeo.uvs, 2));
  }
  if (modGeo.indices.length > 0) {
    result.setIndex(new THREE.BufferAttribute(modGeo.indices, 1));
  }

  // Morph targets — prefer extras override, else fall back to what the
  // modifier pipeline propagated on `modGeo`.
  const morphArrays = extras?.morphArrays ?? modGeo.morphArrays;
  const morphNames = extras?.morphNames ?? modGeo.morphNames;
  const morphTargetsRelative =
    extras?.morphTargetsRelative ?? modGeo.morphTargetsRelative;
  if (morphArrays && morphArrays.length > 0) {
    result.morphAttributes.position = morphArrays.map(
      (arr) => new THREE.BufferAttribute(arr, 3),
    );
    if (morphTargetsRelative !== undefined) {
      result.morphTargetsRelative = morphTargetsRelative;
    }
    if (morphNames) {
      (result.userData as Record<string, unknown>).morphNames = morphNames;
    }
  }

  // Skin weights — propagated through the pipeline on modGeo
  if (modGeo.skinIndex && modGeo.skinWeight) {
    result.setAttribute(
      "skinIndex",
      new THREE.BufferAttribute(modGeo.skinIndex, 4),
    );
    result.setAttribute(
      "skinWeight",
      new THREE.BufferAttribute(modGeo.skinWeight, 4),
    );
  }

  // Material groups — prefer extras override, else pipeline-propagated
  const groups = extras?.groups ?? modGeo.groups;
  applyGeometryGroupsWithDefaultFill(result, groups);

  return result;
}
